/**
 * TocSidebar - parse Dieu/Chuong heading tu raw HTML va render TOC link.
 *
 * Spec: specs/M2-EXECUTION-PLAN-v2.md §4 P-A5 AC-8 + AC-11.
 *
 * Pipeline:
 *   1. Regex parse `<strong>Điều N</strong>` or `<strong>Chương N</strong>` ->
 *      `{kind, no, title, anchor_id}[]`.
 *   2. Inject `id="dieu-N"` / `id="chuong-N"` anchor INTO sanitized HTML before
 *      iframe srcdoc render (handled by parent RawHtmlViewer).
 *   3. Render TOC link list. Click -> postMessage to iframe to scroll-spy.
 *
 * Mobile (<720px): hide sidebar, expose bottom-sheet trigger button. AC-11.
 *
 * IntersectionObserver: cannot observe inside iframe directly; we rely on iframe
 * postMessage event (RawHtmlViewer injects script-less mechanism via hash change
 * + dispatch). MVP: highlight active anchor via TOC click only.
 */

import { useEffect, useState } from 'react';

export interface TocEntry {
  kind: 'chuong' | 'dieu';
  number: string;
  title: string;
  anchor_id: string;
}

interface Props {
  entries: TocEntry[];
  activeAnchor: string | null;
  onJump: (anchorId: string) => void;
}

/**
 * Parse TOC entries tu HTML body voi 2 strategy:
 *   - Heading tag: `<h2|h3|h4>Điều N. Title</h2>`
 *   - Strong inline: `<strong>Điều N</strong>. Title` (vbpl.vn pattern)
 *
 * Tra ve mang sap xep theo thu tu xuat hien.
 */
export function parseTocFromHtml(html: string): TocEntry[] {
  const out: TocEntry[] = [];
  const seen = new Set<string>();
  if (!html) return out;

  // Pattern A: <strong>Chương|Điều N</strong> followed by title
  // We capture position and grab next 120 char as title context (best-effort).
  const reStrong = /<strong[^>]*>\s*(Chương|Điều|Chuong|Dieu)\s+([IVXLCDM0-9]+)\s*\.?\s*<\/strong>([^<]{0,200})/gi;
  let m: RegExpExecArray | null;
  while ((m = reStrong.exec(html)) !== null) {
    const kindRaw = m[1].toLowerCase();
    const kind: 'chuong' | 'dieu' =
      kindRaw === 'chuong' || kindRaw === 'chương' ? 'chuong' : 'dieu';
    const number = m[2].trim();
    const title = (m[3] ?? '')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 140);
    const anchor_id = kind === 'chuong' ? `chuong-${number}` : `dieu-${number}`;
    if (seen.has(anchor_id)) continue;
    seen.add(anchor_id);
    out.push({ kind, number, title, anchor_id });
  }

  // Pattern B (fallback for already-injected heading anchor by parser):
  // <h[1-4] id="dieu-N">Điều N. Title</h[1-4]>
  if (out.length === 0) {
    const reHead = /<h[1-4][^>]*id=["']?(dieu|chuong)-(\d+)["']?[^>]*>([\s\S]*?)<\/h[1-4]>/gi;
    let h: RegExpExecArray | null;
    while ((h = reHead.exec(html)) !== null) {
      const kind = h[1].toLowerCase() as 'chuong' | 'dieu';
      const number = h[2];
      const title = h[3]
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 140);
      const anchor_id = `${kind}-${number}`;
      if (seen.has(anchor_id)) continue;
      seen.add(anchor_id);
      out.push({ kind, number, title, anchor_id });
    }
  }

  return out;
}

/**
 * Inject `id="dieu-N"` anchor o moi <strong>Điều N</strong> trong sanitized
 * HTML truoc khi build iframe srcdoc. Wrap voi span anchor neu strong khong
 * mang id duoc.
 *
 * Tra ve HTML string voi anchor injected.
 */
export function injectAnchorIds(html: string): string {
  if (!html) return html;
  let counter = 0;
  return html.replace(
    /<strong([^>]*)>(\s*(Chương|Điều|Chuong|Dieu)\s+([IVXLCDM0-9]+)\s*\.?\s*)<\/strong>/gi,
    (_full, attr: string, inner: string, kindRaw: string, num: string) => {
      counter++;
      const kindLower = kindRaw.toLowerCase();
      const kind = kindLower === 'chuong' || kindLower === 'chương' ? 'chuong' : 'dieu';
      const id = `${kind}-${num}`;
      // If attr already has id, keep it; otherwise prepend our id + class.
      if (/\bid\s*=/.test(attr)) {
        return `<strong${attr}>${inner}</strong>`;
      }
      return `<strong id="${id}" class="dieu-anchor"${attr}>${inner}</strong>`;
    },
  ) + (counter === 0 ? '' : '');
}

export function TocSidebar({ entries, activeAnchor, onJump }: Props) {
  const [isMobile, setIsMobile] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 720px)');
    const handler = () => setIsMobile(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  if (entries.length === 0) return null;

  const ListBody = (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {entries.map((e) => {
        const isActive = activeAnchor === e.anchor_id;
        return (
          <li key={e.anchor_id} style={{ margin: '2px 0' }}>
            <button
              type="button"
              onClick={() => {
                onJump(e.anchor_id);
                if (isMobile) setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                border: 0,
                borderLeft: isActive
                  ? '2px solid var(--asklaw-orange)'
                  : '2px solid transparent',
                background: 'transparent',
                color: isActive ? 'var(--asklaw-orange)' : 'var(--asklaw-ink-soft)',
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                lineHeight: 1.4,
                minHeight: 36,
              }}
            >
              <span style={{ fontWeight: 700, marginRight: 6 }}>
                {e.kind === 'chuong' ? 'Chương' : 'Điều'} {e.number}
              </span>
              {e.title ? (
                <span style={{ color: 'var(--asklaw-muted)', fontWeight: 500 }}>
                  {e.title.length > 40 ? e.title.slice(0, 40) + '...' : e.title}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Mở mục lục"
          style={{
            position: 'fixed',
            right: 16,
            bottom: 24,
            zIndex: 40,
            padding: '12px 18px',
            borderRadius: 9999,
            border: 0,
            background: 'var(--asklaw-orange)',
            color: '#fff',
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 13,
            boxShadow: 'var(--shadow-card-hover)',
            minHeight: 44,
            minWidth: 44,
          }}
        >
          Mục lục ({entries.length})
        </button>
        {open ? (
          <div
            role="dialog"
            aria-label="Mục lục văn bản"
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              zIndex: 60,
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxHeight: '75vh',
                overflowY: 'auto',
                background: 'var(--asklaw-paper)',
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                padding: '18px 16px 24px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 16, color: 'var(--asklaw-ink)' }}>
                  Mục lục
                </h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Đóng mục lục"
                  style={{
                    background: 'transparent',
                    border: 0,
                    fontSize: 22,
                    cursor: 'pointer',
                    minHeight: 44,
                    minWidth: 44,
                  }}
                >
                  x
                </button>
              </div>
              {ListBody}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <aside
      aria-label="Mục lục văn bản"
      style={{
        position: 'sticky',
        top: 128,
        maxHeight: 'calc(100vh - 160px)',
        overflowY: 'auto',
        paddingRight: 12,
        borderRight: '1px solid var(--asklaw-border)',
        fontSize: 13,
      }}
    >
      <h2
        style={{
          margin: '0 0 10px',
          fontSize: 13.5,
          fontWeight: 700,
          color: 'var(--asklaw-ink)',
          fontFamily: 'var(--font-sans)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Mục lục ({entries.length})
      </h2>
      {ListBody}
    </aside>
  );
}
